import React, { useState, useRef } from "react";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";

import Button from "@material-ui/core/Button";

import Dialog from "src/components/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Card, Link, Tooltip } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import CompareArrowsOutlinedIcon from "@material-ui/icons/CompareArrowsOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { DropzoneAreaBase } from "material-ui-dropzone";
import { heicTo, isHeic } from "heic-to";
// fix-vim-highlight = }

const MAX_RECEIPT_FILE_SIZE = 6 * 1024 * 1024; // 6 MB

const PdfMimeType = "application/pdf";

/**
 * Receipts can be images or PDFs. Storage paths keep the original extension
 * (`{uid}/transactionReceipts/{id}_{name}`), so callers can tell which kind of
 * receipt a transaction has without downloading it.
 */
export function isPdfReceiptFilename(filename: string) {
  return filename.toLowerCase().endsWith(".pdf");
}

function isPdfFile(file: File) {
  return file.type === PdfMimeType || isPdfReceiptFilename(file.name);
}

const useStyles = makeStyles((theme) => ({
  receiptCard: {
    width: "100%",
    minHeight: "200px",
    minWidth: "300px",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
  },
  deleteButton: {
    position: "absolute",
    right: theme.spacing(5),
    color: theme.palette.error.main,
  },
  changeImageButton: {
    position: "absolute",
    right: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: "0px",
  },
  receiptImage: {
    maxWidth: "100%",
    maxHeight: "500px",
  },
  errorButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  receiptPdf: {
    display: "block",
    width: "100%",
    height: "500px",
    border: "none",
  },
}));

export interface ReceiptDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (newReceiptFile?: File) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  receiptFilename?: string;
  /** Whether the existing receipt is a PDF rather than an image. */
  receiptIsPdf?: boolean;
}

function ReceiptDialogContent({
  onCancel,
  onConfirm,
  onDelete,
  receiptFilename,
  receiptIsPdf,
}: Omit<ReceiptDialogProps, "open">) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    receiptFilename
  );
  const [previewIsPdf, setPreviewIsPdf] = useState(!!receiptIsPdf);
  const [newReceiptFile, setNewReceiptFile] = useState<File | undefined>(
    undefined
  );
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const convertLegacy = async (source: File) => {
    let image = await createImageBitmap(source);

    let canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;

    let context = canvas.getContext("2d");
    context?.drawImage(image, 0, 0);

    let result: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob != null) {
            resolve(blob);
          } else {
            reject(new Error("Failed to convert file"));
          }
        },
        "image/jpeg",
        1
      );
    });

    image.close();
    const fileNameWithExtension =
      source.name.substring(0, source.name.lastIndexOf(".")) + ".jpg";
    return new File([result], fileNameWithExtension, { type: "image/jpeg" });
  };

  const convertHeic = async (file: File) => {
    return await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.5,
    });
  };

  const addReceipt = async (file: File) => {
    if (file.size > MAX_RECEIPT_FILE_SIZE) {
      setUploadError("Please upload a receipt smaller than 6 MB.");
      return;
    }
    setUploadError(undefined);

    if (isPdfFile(file)) {
      setPreviewUrl(URL.createObjectURL(file));
      setPreviewIsPdf(true);
      setNewReceiptFile(file);
      return;
    }

    let convertedFile: File;
    try {
      if (await isHeic(file)) {
        const convertedBlob = await convertHeic(file);
        convertedFile = new File(
          [convertedBlob],
          file.name.substring(0, file.name.lastIndexOf(".")) + ".jpg",
          { type: "image/jpeg" }
        );
      } else {
        convertedFile = await convertLegacy(file);
      }
    } catch (e) {
      setUploadError(
        "Unsupported file type. Please upload an image or PDF file."
      );
      return;
    }

    setPreviewUrl(URL.createObjectURL(convertedFile));
    setPreviewIsPdf(false);
    setNewReceiptFile(convertedFile);
  };

  return (
    <>
      <>
        <DialogTitle>
          {receiptFilename ? "View/Update Receipt" : "Add Receipt"}
          {previewUrl && (
            <IconButton
              onClick={() => setConfirmDialog(true)}
              className={classes.deleteButton}
              disabled={loading}
            >
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          )}
        </DialogTitle>
        <>
          <DialogContent>
            <Card className={classes.receiptCard}>
              <input
                accept="image/*,application/pdf,.pdf,.heic,.heif"
                id="contained-button-file"
                multiple
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={async (e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    await addReceipt(e.target.files[0]);
                  }
                }}
              />
              {previewUrl ? (
                <Box position="relative" width="100%">
                  {previewIsPdf ? (
                    <>
                      <iframe
                        title="Receipt"
                        src={previewUrl}
                        className={classes.receiptPdf}
                      />
                      <Box display="flex" justifyContent="center" mt={1}>
                        {/* Mobile browsers only render the first page of an
                        embedded PDF, so always offer the full document. */}
                        <Link
                          href={previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                        >
                          Open PDF in a new tab
                        </Link>
                      </Box>
                    </>
                  ) : (
                    <img
                      alt="Receipt"
                      src={previewUrl}
                      className={classes.receiptImage}
                    />
                  )}
                  <Box position="absolute" top={10} right={10} zIndex={1}>
                    <IconButton
                      onClick={triggerFileInput}
                      className={classes.changeImageButton}
                      disabled={loading}
                      size="small"
                    >
                      <CompareArrowsOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <DropzoneAreaBase
                  acceptedFiles={[
                    "image/*",
                    PdfMimeType,
                    ".pdf",
                    ".heic",
                    ".heif",
                  ]}
                  maxFileSize={MAX_RECEIPT_FILE_SIZE}
                  showAlerts={false}
                  onDropRejected={(files) => {
                    if (!files?.length) return;
                    const [file] = files;
                    if (file.size > MAX_RECEIPT_FILE_SIZE) {
                      setUploadError(
                        "Please upload a receipt smaller than 6 MB."
                      );
                      return;
                    }
                    setUploadError(
                      "Unsupported file type. Please upload an image or PDF file."
                    );
                  }}
                  dropzoneText={
                    "Drag and drop your receipt image or PDF here or click"
                  }
                  onAdd={async (fileObjs) => {
                    if (fileObjs && fileObjs.length > 0) {
                      await addReceipt(fileObjs[0].file);
                    }
                  }}
                  fileObjects={[]}
                  filesLimit={1}
                />
              )}
            </Card>
            {uploadError ? (
              <Box mt={1}>
                <Typography color="error" variant="body2">
                  {uploadError}
                </Typography>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={async () => {
                setLoading(true);
                onCancel();
                setPreviewUrl(undefined);
                setNewReceiptFile(undefined);
                setLoading(false);
              }}
              disabled={loading}
            >
              Cancel
            </Button>

            <Tooltip
              title="To change this receipt, click the swap icon in the upper right of the preview."
              disableHoverListener={
                !((loading || !newReceiptFile) && receiptFilename)
              }
              disableFocusListener={
                !((loading || !newReceiptFile) && receiptFilename)
              }
              disableTouchListener={
                !((loading || !newReceiptFile) && receiptFilename)
              }
            >
              <span>
                <Button
                  disabled={loading || !newReceiptFile}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await onConfirm(newReceiptFile);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  color="primary"
                  variant="contained"
                >
                  {receiptFilename ? "Update" : "Add"}
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </>
      </>
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Are you sure you want to delete this receipt?</DialogTitle>
        <DialogActions>
          <Button disabled={loading} onClick={() => setConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setLoading(true);
              try {
                await onDelete();
                setPreviewUrl(undefined);
                setNewReceiptFile(undefined);
                setConfirmDialog(false);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className={classes.errorButton}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ReceiptDialog({ open, onCancel, ...props }: ReceiptDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <ReceiptDialogContent {...props} onCancel={onCancel} />
    </Dialog>
  );
}

export default ReceiptDialog;
